import React, { useState, useEffect, useRef } from 'react';
import Styles from './Styles.css.js';
import LinkBar from './LinkBar';
import LogoBar from './LogoBar';
import Banner from './Banner';
import Offers from './Offers';
import { useMediaQuery } from 'react-responsive';
import Radium from 'radium';
import useInsertNewUser from '../hooks/useInsertNewUser';
import pingHitStreet from '../utils/hit_street';
import usePchLookup from '../hooks/usePchLookup';
import axios from 'axios';
import usePushProviders from '../hooks/usePushProviders';

const cancelToken = axios.CancelToken;
const source = cancelToken.source();

const getUserLocation = async() => {
    let geoLink = 'https://geolocation-db.com/json/0f761a30-fe14-11e9-b59f-e53803842572';
    try{
        const res = await axios({
            method: 'get',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            url: geoLink,
            cancelToken: source.token
        });
        if(res.status !== 200) {
            return {status: 'failed', message: 'Error getting user location.'};
        }
        return res.data;
    } catch(err) {
        if (axios.isCancel(err)) {
            return {status: 'failed', message: 'axios request cancelled'};
        }
        return {status: 'failed', message: err};
    }
};

const App = Radium(({ campaign }) => {
	const [ myURL ] = useState(new URL(window.location.href));
    const isMobile = useMediaQuery({ maxWidth: 667 });
    const [ tracking, setUserContext ] = useState({});
    // const [ shouldExecute, setShouldExecutePost ] = useState(false);
    const pchUser = usePchLookup(tracking.PT1, tracking.PT2, tracking.HSID, tracking.EMAIL);
    const retry_count = useRef(3);
    const showPchLogo = useRef(true);
    // const hasTracking = useRef(false);
    const hasPchData = useRef(false);

    usePushProviders();

    const getLocation = async () => {
        const res = await getUserLocation();
        if(res.status === 'failed' && retry_count.current > 0) {
            retry_count.current--;
            getLocation();
            return;
        }
        if(res.status === 'failed' && retry_count.current === 0){
            return {state:'FAILED', IPv4: 'FAILED'};
        };
        return res;
    };

    useInsertNewUser(tracking);

    const fallbackType = () => {
        switch(campaign) {
            case 'american':
                return 'great_american';
            case 'gold':
                return 'gold_seal';
            case 'dream':
                return 'dream_home';
            case 'super':
                return 'supermarket';
            default:
                return 'gold_seal';
        };
    };

    const buildNewUser = async () => {
        const trackingInit = {
            HSID: myURL.searchParams.get('hsid') || 0,
            PID: myURL.searchParams.get('pid') || 3317,
            SID: myURL.searchParams.get('sid') || 7572,
            OID: myURL.searchParams.get('oid') || 50,
            UID: myURL.searchParams.get('uid') || '',
            EID: myURL.searchParams.get('eid') || 'organic',
            SE: myURL.searchParams.get('se') || '',
            KWD: myURL.searchParams.get('kwd') || '',
            PACID: myURL.searchParams.get('pacid') || '',
            PT1: myURL.searchParams.get('pt1') || null,
            PT2: myURL.searchParams.get('pt2') || null,
            GCLID: myURL.searchParams.get('gclid') || '',
            EMAIL: myURL.searchParams.get('email') || '',
            VERTICAL: myURL.searchParams.get('vertical') || 'direct',
            TYPE: myURL.searchParams.get('type') || fallbackType(),
            r: myURL.searchParams.get('r') || null,
            theme: campaign,
            auth_group: myURL.searchParams.get('group') || 'bk'
        }
        const clickId = await pingHitStreet(trackingInit);
        const geoData = await getLocation();
        const trackedProps = {
            ...trackingInit,
            HSID: clickId,
            hit_street: true,
            location: geoData?.state || geoData?.country_name || 'Unknown',
            ip: geoData?.IPv4
        };
        setUserContext(trackedProps); 
    };

    useEffect(() => {
        buildNewUser();
        // eslint-disable-next-line
    }, []);
    
    useEffect(() => {
        if(isMobile && campaign === 'gold') {
            showPchLogo.current = false;
        };
        // eslint-disable-next-line
    }, [isMobile, campaign]);
	
    useEffect(() => {
		if(!!pchUser && tracking.hit_street && !hasPchData.current) {
			const updatedUserData = {
                ...tracking,
                ...pchUser
            }
            setUserContext(updatedUserData);
            hasPchData.current = true;
		};
        return () => hasPchData.current = false;
		// eslint-disable-next-line
	}, [pchUser, tracking.hit_street]);

    const campaignStyle = Object.assign({},
        Styles.app,
        Styles[campaign]
    );
	return (
		<div key='app-key' style={campaignStyle}>
            <LinkBar theme={campaign}/>
            {showPchLogo.current && <LogoBar />}
            <Banner campaign={campaign}/>
			<Offers tracking={tracking}/>
		</div>
	);
});

export default App;